import axios from 'axios';
import { installOfficeHeader } from './officeSelection';

// Backend URL from environment
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

if (!BACKEND_URL) {
  const errorMsg = `
CRITICAL CONFIGURATION ERROR:
REACT_APP_BACKEND_URL is not set. Check frontend/.env
`;
  console.error(errorMsg);
  throw new Error('REACT_APP_BACKEND_URL is not configured');
}

// Validate URL format
try {
  new URL(BACKEND_URL);
} catch (e) {
  throw new Error(`Invalid backend URL format: ${BACKEND_URL}`);
}

console.log('Auth API using backend URL:', BACKEND_URL);
console.log('Environment REACT_APP_BACKEND_URL:', process.env.REACT_APP_BACKEND_URL);
console.log('Current origin:', typeof window !== 'undefined' ? window.location.origin : 'N/A');

// Create axios instance for auth
const authAxios = axios.create({
  baseURL: BACKEND_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Create axios instance for practice management
const practiceAxios = axios.create({
  baseURL: BACKEND_URL,
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to practice requests
practiceAxios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('dentalToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
const handleApiError = (error) => {
  if (error.response?.status === 401) {
    // Token expired or invalid
    localStorage.removeItem('dentalToken');
    window.location.href = '/login';
  }
  return Promise.reject(error);
};

authAxios.interceptors.response.use(
  (response) => response,
  handleApiError
);

practiceAxios.interceptors.response.use(
  (response) => response,
  handleApiError
);

export const authApi = {
  // Authentication endpoints
  login: async (email, password) => {
    try {
      const response = await authAxios.post('/api/auth/login', { email, password });
      return response.data;
    } catch (error) {
      console.error('Login error:', error.response?.data || error.message);
      throw error;
    }
  },

  registerPractice: async (practiceData) => {
    const response = await authAxios.post('/api/auth/register-practice', {
      practiceName: practiceData.practiceName,
      email: practiceData.email,
      phone: practiceData.phone,
      website: practiceData.website,
      adminFirstName: practiceData.adminFirstName,
      adminLastName: practiceData.adminLastName,
      adminPassword: practiceData.adminPassword,
      street: practiceData.street,
      city: practiceData.city,
      state: practiceData.state,
      zipCode: practiceData.zipCode
    });
    return response.data;
  },

  getCurrentUser: async (token) => {
    const response = await authAxios.get('/api/auth/me', {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data;
  },

  invitePatient: async (email, firstName, lastName) => {
    const token = localStorage.getItem('dentalToken');
    const response = await authAxios.post('/api/auth/invite-patient', 
      { email, firstName, lastName },
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
    return response.data;
  },

  getProcedures: async (specialty = null) => {
    const cacheKey = specialty ? `procedures_${specialty}` : 'procedures';
    const cached = getCached(cacheKey);
    if (cached) return cached;
    const url = specialty ? `/api/public/procedures?specialty=${specialty}` : '/api/public/procedures';
    const response = await axios.get(`${BACKEND_URL}${url}`);
    setCache(cacheKey, response.data);
    return response.data;
  }
};

// Simple in-memory cache for frequently-accessed data
// Prevents redundant API calls during page transitions
const _cache = {};
const CACHE_TTL = 180000; // 3 minutes

function getCached(key) {
  const entry = _cache[key];
  if (entry && Date.now() - entry.ts < CACHE_TTL) return entry.data;
  return null;
}

function setCache(key, data) {
  _cache[key] = { data, ts: Date.now() };
}

// Call this after any mutation (create/update/delete) to bust relevant caches
export function bustCache(prefix) {
  Object.keys(_cache).forEach(k => { if (!prefix || k.startsWith(prefix)) delete _cache[k]; });
}

export const practiceApi = {
  // Practice management endpoints
  getDashboard: async () => {
    const cached = getCached('dashboard');
    if (cached) return cached;
    const response = await practiceAxios.get('/api/practice/dashboard');
    setCache('dashboard', response.data);
    return response.data;
  },

  getPatients: async () => {
    const cached = getCached('patients');
    if (cached) return cached;
    const response = await practiceAxios.get('/api/practice/patients');
    setCache('patients', response.data);
    return response.data;
  },

  createPatient: async (patientData) => {
    bustCache('patients');
    bustCache('dashboard');
    const response = await practiceAxios.post('/api/practice/patients', patientData);
    return response.data;
  },

  assignProcedure: async (assignmentData) => {
    bustCache('dashboard');
    const response = await practiceAxios.post('/api/practice/assign-procedure', assignmentData);
    return response.data;
  },

  assignMultipleProcedures: async (assignmentData) => {
    bustCache('dashboard');
    const response = await practiceAxios.post('/api/practice/assign-multiple-procedures', assignmentData);
    return response.data;
  },

  updateBranding: async (brandingData) => {
    bustCache('dashboard');
    const response = await practiceAxios.put('/api/practice/branding', brandingData);
    return response.data;
  },

  updatePractice: async (updateData) => {
    const response = await practiceAxios.put('/api/practice/update', updateData);
    return response.data;
  },

  getExportData: async () => {
    const response = await practiceAxios.get('/api/practice/export-data');
    return response.data;
  },

  getProcedureAssignment: async (assignmentId) => {
    const response = await practiceAxios.get(`/api/practice/assignment/${assignmentId}`);
    return response.data;
  },

  updateProcedureAssignment: async (assignmentId, updateData) => {
    const response = await practiceAxios.put(`/api/practice/assignment/${assignmentId}`, updateData);
    return response.data;
  },


  getProblemAssignment: async (assignmentId) => {
    const response = await practiceAxios.get(`/api/problems-emergencies/assignment/${assignmentId}`);
    return response.data;
  },

  updateProblemAssignment: async (assignmentId, updateData) => {
    const response = await practiceAxios.put(`/api/problems-emergencies/assignments/${assignmentId}`, updateData);
    return response.data;
  },

  getPracticeDoctors: async () => {
    const cached = getCached('doctors');
    if (cached) return cached;
    const response = await practiceAxios.get('/api/practice/doctors');
    setCache('doctors', response.data);
    return response.data;
  },

  requestNewProcedure: async (requestData) => {
    const response = await practiceAxios.post('/api/practice/request-procedure', requestData);
    return response.data;
  },

  updatePatient: async (patientId, patientData) => {
    bustCache('patients');
    bustCache('dashboard');
    const response = await practiceAxios.put(`/api/practice/patients/${patientId}`, patientData);
    return response.data;
  },

  deletePatient: async (patientId, hardDelete = false) => {
    bustCache('patients');
    bustCache('dashboard');
    const response = await practiceAxios.put(`/api/practice/patients/${patientId}`, {
      action: hardDelete ? "remove" : "deactivate",
      confirmDelete: true
    });
    return response.data;
  },

  // Dentist management
  getDentists: async () => {
    const cached = getCached('dentists');
    if (cached) return cached;
    const response = await practiceAxios.get('/api/practice/dentists');
    setCache('dentists', response.data);
    return response.data;
  },

  addDentist: async (dentistData) => {
    const response = await practiceAxios.post('/api/practice/dentists', dentistData);
    bustCache('dentists');
    return response.data;
  },

  updateDentist: async (dentistId, dentistData) => {
    const response = await practiceAxios.put(`/api/practice/dentists/${dentistId}`, dentistData);
    bustCache('dentists');
    return response.data;
  },

  removeDentist: async (dentistId) => {
    const response = await practiceAxios.delete(`/api/practice/dentists/${dentistId}`);
    bustCache('dentists');
    return response.data;
  },

  // Practice-specific procedure management
  getPracticeProcedures: async () => {
    const cached = getCached('practiceProcedures');
    if (cached) return cached;
    const response = await practiceAxios.get('/api/practice/procedures');
    setCache('practiceProcedures', response.data);
    return response.data;
  },

  getPracticeProcedure: async (procedureId) => {
    const response = await practiceAxios.get(`/api/practice/procedures/${procedureId}`);
    return response.data;
  },

  customizeProcedure: async (procedureId, customization) => {
    const response = await practiceAxios.post(`/api/practice/procedures/${procedureId}/customize`, customization);
    return response.data;
  },

  removeProcedureCustomization: async (procedureId) => {
    const response = await practiceAxios.delete(`/api/practice/procedures/${procedureId}/customize`);
    return response.data;
  },

  emailPDF: async (emailData) => {
    const response = await practiceAxios.post('/api/practice/email-pdf', emailData);
    return response.data;
  },

  downloadPDF: async (pdfData) => {
    const response = await practiceAxios.post('/api/practice/download-pdf', pdfData, {
      responseType: 'blob' // Important for PDF downloads
    });
    return response;
  },

  // smsPDF method removed - SMS functionality no longer available

  getExportActivities: async (queryParams = '') => {
    const response = await practiceAxios.get(`/api/practice/export-activities?${queryParams}`);
    return response.data;
  },

  exportCorrespondence: async (exportRequest) => {
    const response = await practiceAxios.post('/api/practice/export-correspondence', exportRequest, {
      responseType: 'blob' // Important for file downloads
    });
    return response;
  },

  logActivity: async (activityData) => {
    const response = await practiceAxios.post('/api/practice/log-activity', activityData);
    return response.data;
  },

  importPatientsCSV: async (file) => {
    console.log('🔍 API: Starting CSV import...');
    console.log('🔍 API: File object:', file);
    
    const formData = new FormData();
    formData.append('file', file);
    
    console.log('🔍 API: FormData created');
    console.log('🔍 API: FormData entries:');
    for (let [key, value] of formData.entries()) {
      console.log(`  ${key}:`, value);
    }
    
    try {
      console.log('🔍 API: Making request to /api/practice/import-patients-csv');
      
      const response = await practiceAxios.post('/api/practice/import-patients-csv', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      console.log('🔍 API: Response received:', response);
      console.log('🔍 API: Response status:', response.status);
      console.log('🔍 API: Response data:', response.data);
      
      return response.data;
    } catch (error) {
      console.error('🔍 API: Request failed:', error);
      console.error('🔍 API: Error response:', error.response);
      console.error('🔍 API: Error status:', error.response?.status);
      console.error('🔍 API: Error data:', error.response?.data);
      throw error;
    }
  },

  downloadPatientCSVTemplate: async () => {
    const response = await practiceAxios.get('/api/practice/patient-csv-template', {
      responseType: 'blob'
    });
    return response.data;
  },

  submitSupportRequest: async (supportData) => {
    const response = await practiceAxios.post('/api/support/request', supportData);
    return response.data;
  },

  getSupportRequests: async () => {
    const response = await practiceAxios.get('/api/support/requests');
    return response.data;
  },

  deleteSupportRequest: async (requestId) => {
    const response = await practiceAxios.delete(`/api/support/requests/${requestId}`);
    return response.data;
  },

  replyToSupportRequest: async (requestId, replyText) => {
    const response = await practiceAxios.post(`/api/support/requests/${requestId}/reply`, {
      reply_text: replyText
    });
    return response.data;
  },

  // Med Cards endpoints
  getMedCards: async () => {
    const response = await practiceAxios.get('/api/med-cards');
    return response.data;
  },

  getMedCard: async (cardId) => {
    const response = await practiceAxios.get(`/api/med-cards/${cardId}`);
    return response.data;
  },

  updateMedCard: async (cardId, customContent) => {
    const response = await practiceAxios.put(`/api/med-cards/${cardId}`, { customContent });
    return response.data;
  },

  assignMedCards: async (assignmentData) => {
    bustCache('dashboard');
    const response = await practiceAxios.post('/api/med-cards/assign', assignmentData);
    return response.data;
  },

  getMedCardDisclaimerStatus: async () => {
    const response = await practiceAxios.get('/api/med-cards/disclaimer/status');
    return response.data;
  },

  acceptMedCardDisclaimer: async (signature) => {
    const response = await practiceAxios.post('/api/med-cards/disclaimer/accept', {
      signature,
      acceptedAt: new Date().toISOString()
    });
    return response.data;
  },

  exportMedCardAssignments: async () => {
    const response = await practiceAxios.get('/api/med-cards/assignments/export');
    return response.data;
  },

  emailMedCard: async (cardId, patientEmail, patientName, customContent = null) => {
    const response = await practiceAxios.post('/api/med-cards/email', {
      card_id: cardId,
      patient_email: patientEmail,
      patient_name: patientName,
      custom_content: customContent
    });
    return response.data;
  },

  // Management Forms endpoints
  getManagementForms: async () => {
    const response = await practiceAxios.get('/api/management-forms');
    return response.data;
  },

  createManagementForm: async (formData) => {
    const response = await practiceAxios.post('/api/management-forms', formData);
    return response.data;
  },

  updateManagementForm: async (formId, formData) => {
    const response = await practiceAxios.put(`/api/management-forms/${formId}`, formData);
    return response.data;
  },

  deleteManagementForm: async (formId) => {
    const response = await practiceAxios.delete(`/api/management-forms/${formId}`);
    return response.data;
  },

  assignManagementForm: async (formId, assignmentData) => {
    const response = await practiceAxios.post(`/api/management-forms/${formId}/assign`, assignmentData);
    return response.data;
  },

  getPatientManagementForms: async (patientId) => {
    const response = await practiceAxios.get(`/api/management-forms/assignments/patient/${patientId}`);
    return response.data;
  },

  unassignManagementForm: async (assignmentId) => {
    const response = await practiceAxios.delete(`/api/management-forms/assignments/${assignmentId}`);
    return response.data;
  }
};

export const patientsApi = {
  // Patient-specific endpoints
  getDashboard: async () => {
    const token = localStorage.getItem('dentalToken');
    const response = await axios.get(`${BACKEND_URL}/api/patients/dashboard`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data;
  },

  getProcedure: async (assignmentId) => {
    const token = localStorage.getItem('dentalToken');
    const response = await axios.get(`${BACKEND_URL}/api/patients/procedures/${assignmentId}`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data;
  },

  trackDownload: async (assignmentId) => {
    const token = localStorage.getItem('dentalToken');
    const response = await axios.post(`${BACKEND_URL}/api/patients/procedures/${assignmentId}/download`, {}, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data;
  },

  updateProcedureOverview: async (procedureId, overview) => {
    const token = localStorage.getItem('dentalToken');
    const response = await axios.put(`${BACKEND_URL}/api/public/procedures/${procedureId}/overview`, 
      { overview }, 
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    return response.data;
  },

  setupPassword: async (setupData) => {
    const response = await axios.post(`${BACKEND_URL}/api/auth/patient-setup`, setupData);
    return response.data;
  }
};

// Create axios instance for admin
const apiClient = axios.create({
  baseURL: BACKEND_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to admin requests
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export const adminApi = {
  login: async (credentials) => {
    const response = await apiClient.post('/admin/login', credentials);
    return response.data;
  },

  getPractices: async (limit = 10, offset = 0) => {
    const response = await apiClient.get(`/admin/practices?limit=${limit}&offset=${offset}`);
    return response.data;
  },

  getDashboardStats: async () => {
    const response = await apiClient.get('/admin/dashboard-stats');
    return response.data;
  },

  getAllSupportRequests: async () => {
    const response = await apiClient.get('/api/support/admin/all-requests');
    return response.data;
  },

  respondToSupportRequest: async (requestId, responseData) => {
    const response = await apiClient.post(`/api/support/admin/respond/${requestId}`, responseData);
    return response.data;
  }
};
installOfficeHeader(authAxios, practiceAxios, apiClient);
